/**
 * OutboundMessageButton — split button with smart default channel + modal override.
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Dropdown, Space } from "antd";
import { DownOutlined, WhatsAppOutlined } from "@ant-design/icons";
import OutboundMessageModal from "./OutboundMessageModal.jsx";
import {
  getDefaultChannelId,
  openOutboundWhatsApp,
  setDefaultChannelId,
} from "../utils/outboundMessaging.js";

/**
 * @param {Object} props
 * @param {() => Promise<Array>} props.loadChannels
 * @param {(channel: Object) => string} props.buildMessage
 * @param {'fixed'|'channel'} [props.recipientMode]
 * @param {string} [props.recipientPhone]
 * @param {string} [props.storageKey]
 * @param {string} [props.buttonLabel]
 * @param {string} props.modalTitle
 * @param {string} [props.modalDescription]
 * @param {string} [props.recipientLabel]
 * @param {string} [props.channelListLabel]
 * @param {string} [props.emptyChannelsMessage]
 * @param {string} [props.searchPlaceholder]
 * @param {string} [props.size]
 * @param {boolean} [props.disabled]
 * @param {(channel: Object) => void} [props.onOpened]
 */
const OutboundMessageButton = ({
  loadChannels,
  buildMessage,
  recipientMode = "fixed",
  recipientPhone,
  storageKey,
  buttonLabel = "WhatsApp",
  modalTitle,
  modalDescription,
  recipientLabel = "Customer",
  channelListLabel = "Reply as",
  emptyChannelsMessage,
  searchPlaceholder,
  size = "small",
  disabled = false,
  onOpened,
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [actionError, setActionError] = useState(null);
  const channelsRef = useRef([]);

  const ensureChannels = useCallback(async () => {
    if (channelsRef.current.length > 0) {
      return channelsRef.current;
    }

    setLoading(true);
    setActionError(null);
    try {
      const result = await loadChannels();
      const list = Array.isArray(result) ? result : [];
      channelsRef.current = list;
      return list;
    } catch (error) {
      setActionError(error.message || "Failed to load channels");
      return [];
    } finally {
      setLoading(false);
    }
  }, [loadChannels]);

  useEffect(() => {
    channelsRef.current = [];
  }, [loadChannels]);

  const resolveDefaultChannel = (channels) => {
    if (!channels.length) return null;
    const savedId = storageKey ? getDefaultChannelId(storageKey) : null;
    if (savedId) {
      const saved = channels.find((channel) => channel.id === savedId);
      if (saved) return saved;
    }
    return channels[0];
  };

  const openWithChannel = (channel) => {
    if (!channel) {
      setModalOpen(true);
      return;
    }

    if (recipientMode === "fixed" && !recipientPhone) {
      setActionError("Customer phone number is missing");
      setModalOpen(true);
      return;
    }

    try {
      const messageText = buildMessage?.(channel) || "";
      openOutboundWhatsApp({
        recipientMode,
        recipientPhone,
        channel,
        messageText,
      });

      if (storageKey) {
        setDefaultChannelId(storageKey, channel.id);
      }

      setActionError(null);
      onOpened?.(channel);
    } catch (error) {
      setActionError(error.message || "Failed to open WhatsApp");
      setModalOpen(true);
    }
  };

  const handlePrimaryClick = async () => {
    const channels = await ensureChannels();
    if (!channels.length) {
      setModalOpen(true);
      return;
    }

    const defaultChannel = resolveDefaultChannel(channels);
    if (storageKey && defaultChannel) {
      openWithChannel(defaultChannel);
      return;
    }

    setModalOpen(true);
  };

  const dropdownItems = [
    {
      key: "choose-line",
      label: "Choose line…",
      onClick: () => setModalOpen(true),
    },
  ];

  return (
    <>
      <Space direction="vertical" size={4}>
        <Button.Group>
          <Button
            type="primary"
            size={size}
            icon={<WhatsAppOutlined />}
            loading={loading}
            disabled={disabled}
            onClick={handlePrimaryClick}
            style={{ backgroundColor: "#25D366", borderColor: "#25D366" }}
          >
            {buttonLabel}
          </Button>
          <Dropdown menu={{ items: dropdownItems }} trigger={["click"]}>
            <Button
              type="primary"
              size={size}
              icon={<DownOutlined />}
              disabled={disabled}
              style={{ backgroundColor: "#25D366", borderColor: "#25D366" }}
            />
          </Dropdown>
        </Button.Group>
        {actionError && (
          <span style={{ color: "#cf1322", fontSize: 12 }}>{actionError}</span>
        )}
      </Space>

      <OutboundMessageModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={modalTitle}
        description={modalDescription}
        recipientMode={recipientMode}
        recipientPhone={recipientPhone}
        recipientLabel={recipientLabel}
        loadChannels={loadChannels}
        buildMessage={buildMessage}
        storageKey={storageKey}
        channelListLabel={channelListLabel}
        emptyChannelsMessage={emptyChannelsMessage}
        searchPlaceholder={searchPlaceholder}
        onOpened={onOpened}
      />
    </>
  );
};

export default OutboundMessageButton;
